import { useEffect, useState } from "react";

/** COMPONENT */
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import { notiError } from "../../../component/notification/notifications";
import DeductTable from "../components/DeductTable";
import ReceiptTable from "../components/ReceiptTable";

/** API */
import DepositRefundApi, {
  DepositRefundFile,
  DepositRefundLog,
  DepositRefundSide,
} from "../../../api/deposit-refund";

/** UTILS */
import { colors } from "../../../constants/colors";
import {
  DEPOSIT_REFUND_LOG_ACTION_LABEL,
  depositRefundStatusBg,
  depositRefundStatusColor,
  depositRefundStatusLabel,
} from "../../../constants/depositRefund";
import { dateTimeToView } from "../../../utils/date.utils";
import { getApiErrorStatus } from "../../../utils/api-error.utils";
import { formatFileSize } from "../../../utils/common";
import { DASH, getDepositRefundError } from "../depositRefund.utils";
import {
  displayFileName,
  downloadDepositRefundFile,
  openDepositRefundFile,
} from "../depositRefundFile.utils";

/** STYLE */
import * as Style from "../depositRefund.style";

type Props = {
  id: number;
  side: DepositRefundSide;
  /** ข้อความใต้หัวข้อ — ฝั่งลูกค้าต่อเลขที่สัญญาเข้ามาด้วย ให้ caller ประกอบมาให้เลย */
  subTitle?: string;
  /**
   * ผู้เช่า/แผงของคำร้อง สำหรับดึงตารางใบเสร็จในรายการที่อนุมัติ
   * ไม่ส่ง = ไม่แสดงตาราง — ฝั่งลูกค้าต้องไม่ส่ง เพราะเส้นใบเสร็จเป็นของเจ้าหน้าที่เท่านั้น (403)
   */
  receiptScope?: { userId: number; stallId?: number | null };
  onClose: () => void;
};

/** ไฟล์ที่กำลังโหลดอยู่ — เก็บ action ด้วยเพื่อให้ spinner ขึ้นตรงปุ่มที่กด */
type FileBusy = { id: number; action: "view" | "download" };

/**
 * modal นี้ชื่อ "ประวัติการพิจารณา" และดีไซน์แสดงเฉพาะรายการที่เป็นการพิจารณา/ยกเลิก/ลบ
 * จึงกรองแถว CREATE (ยื่นคำร้อง) ออก — backend ส่งมาทุกใบตั้งแต่วินาทีที่ยื่น
 * ถ้าไม่กรอง data จะไม่เคยว่าง แล้ว empty state "ยังไม่มีการพิจารณาคำร้องนี้" ไม่มีโอกาสแสดง
 * (BE ยืนยันว่ากรองที่หน้าจอได้ ไม่ต้องแก้ API — ดู deposit-refund-be-reply.md ข้อ B1)
 */
const REVIEW_ACTIONS: DepositRefundLog["action"][] = ["REVIEW", "CANCEL", "DELETE"];

export default function ModalDepositRefundLog({
  id,
  side,
  subTitle,
  receiptScope,
  onClose,
}: Props) {
  const [logs, setLogs] = useState<DepositRefundLog[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<FileBusy | null>(null);

  useEffect(() => {
    /** กัน setState หลัง unmount ตอนผู้ใช้ปิด modal ก่อน response กลับมา */
    let isMounted = true;

    const loadLog = async () => {
      setIsLoading(true);
      try {
        const res = await DepositRefundApi.log(side, id);
        if (!isMounted) return;
        const rows = Array.isArray(res.data?.data) ? [...res.data.data] : [];
        setLogs(
          rows
            .filter((log) => REVIEW_ACTIONS.includes(log.action))
            // เรียงใหม่ -> เก่า เองด้วย ไม่พึ่ง order ที่ backend ส่งมา
            .sort((a, b) => b.id_deposit_refund_log - a.id_deposit_refund_log)
        );
      } catch (error) {
        if (!isMounted) return;
        setLogs([]);
        notiError(getDepositRefundError(error, "ดึงประวัติการพิจารณาไม่สำเร็จ"));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadLog();
    return () => {
      isMounted = false;
    };
  }, [id, side]);

  /**
   * ชื่อผู้ทำรายการที่แสดงได้
   * ฝั่งลูกค้าต้องไม่เห็นชื่อเจ้าหน้าที่ (เหมือนที่ detail modal ซ่อน reviewed_name)
   * ไม่งั้นข้อมูลที่ปิดไว้หน้าหนึ่งจะรั่วอีกหน้าหนึ่ง
   * ยกเว้นแถว CANCEL ที่ผู้ทำรายการคือลูกค้าเอง
   */
  const actorName = (log: DepositRefundLog) => {
    if (side === "CUSTOMER" && log.action === "REVIEW") return "เจ้าหน้าที่";
    return log.action_name || DASH;
  };

  /** 404 ของ backend ตอบ body ว่าง จึงต้องดูจาก status แล้วใส่ข้อความเอง */
  const notiFileError = (error: any, fallback: string) => {
    const status = getApiErrorStatus(error);
    notiError(status === 404 ? "ไม่พบไฟล์แนบที่ต้องการ" : getDepositRefundError(error, fallback));
  };

  const handleView = async (file: DepositRefundFile) => {
    if (busy) return;

    // ต้องเปิดแท็บตรงนี้ ตอนยังอยู่ใน user gesture ไม่งั้นโดน popup blocker หลัง await
    const tab = window.open("", "_blank");
    if (!tab) {
      notiError("เบราว์เซอร์บล็อกการเปิดแท็บใหม่ กรุณาอนุญาต popup หรือกดดาวน์โหลดแทน");
      return;
    }

    setBusy({ id: file.id_deposit_refund_file, action: "view" });
    try {
      await openDepositRefundFile(file, tab);
    } catch (error) {
      tab.close();
      notiFileError(error, "เปิดไฟล์แนบไม่สำเร็จ");
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async (file: DepositRefundFile) => {
    if (busy) return;

    setBusy({ id: file.id_deposit_refund_file, action: "download" });
    try {
      await downloadDepositRefundFile(file);
    } catch (error) {
      notiFileError(error, "ดาวน์โหลดไฟล์แนบไม่สำเร็จ");
    } finally {
      setBusy(null);
    }
  };

  const renderFile = (file: DepositRefundFile) => {
    const fileId = file.id_deposit_refund_file;
    const isViewing = busy?.id === fileId && busy?.action === "view";
    const isDownloading = busy?.id === fileId && busy?.action === "download";
    const isDisabled = !!busy;

    return (
      <Box
        key={fileId}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 1,
          px: 2,
          py: 1.5,
          borderRadius: "8px",
          border: `1px solid ${colors.lightGray}`,
          backgroundColor: colors.white,
        }}
      >
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: colors.textLightGray }} />
        {/* ตัด timestamp ที่ backend เติมหน้าชื่อไฟล์ออก ผู้ใช้ไม่ต้องเห็น */}
        <Typography sx={{ fontSize: 16, flex: 1, minWidth: 0, wordBreak: "break-all" }}>
          {displayFileName(file.file_name)}
        </Typography>
        {file.file_size ? (
          <Typography sx={{ fontSize: 14, color: colors.textLightGray, whiteSpace: "nowrap" }}>
            {formatFileSize(file.file_size)}
          </Typography>
        ) : null}
        <Tooltip title="เปิดดูไฟล์">
          <span>
            <IconButton size="small" disabled={isDisabled} onClick={() => handleView(file)}>
              {isViewing ? (
                <CircularProgress size={16} sx={{ color: colors.themeMainColor }} />
              ) : (
                <VisibilityOutlinedIcon sx={{ fontSize: 20, color: colors.textLightGray }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="ดาวน์โหลดไฟล์">
          <span>
            <IconButton size="small" disabled={isDisabled} onClick={() => handleDownload(file)}>
              {isDownloading ? (
                <CircularProgress size={16} sx={{ color: colors.themeMainColor }} />
              ) : (
                <DownloadOutlinedIcon sx={{ fontSize: 20, color: colors.themeMainColor }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  /** 1 รายการ = การ์ดหัวเรื่อง + การ์ดตารางคำนวณ + กลุ่มไฟล์แนบ ตามดีไซน์ */
  const renderLog = (log: DepositRefundLog) => (
    <Box key={log.id_deposit_refund_log} sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          px: 2,
          py: 2,
          borderRadius: "8px",
          border: `1px solid ${colors.lightGray}`,
          backgroundColor: colors.white,
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            // result เป็น null เมื่อ action ไม่ใช่ REVIEW (ยกเลิก/ลบ) ให้ใช้สีเทากลางๆ
            backgroundColor: depositRefundStatusColor(log.result || ""),
            flexShrink: 0,
            mt: "7px",
          }}
        />
        <Box minWidth={0} sx={{ flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                {DEPOSIT_REFUND_LOG_ACTION_LABEL[log.action] || log.action}
              </Typography>
              {log.result && (
                <Style.StatusChip
                  chipColor={depositRefundStatusColor(log.result)}
                  chipBg={depositRefundStatusBg(log.result)}
                  sx={{ fontSize: 14, minWidth: "unset", px: 1.5, py: "2px" }}
                >
                  {depositRefundStatusLabel(log.result, side === "CUSTOMER")}
                </Style.StatusChip>
              )}
            </Box>
            <Typography sx={{ fontSize: 14, color: colors.textLightGray, whiteSpace: "nowrap" }}>
              {/* action_date เป็น DATETIME จริง ใช้ timezone เครื่องได้ ไม่ต้อง force Asia/Bangkok */}
              {log.action_date ? dateTimeToView(log.action_date) : DASH}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 14, color: colors.textLightGray, mt: 0.5 }}>
            {`โดย ${actorName(log)}`}
          </Typography>

          {log.note && (
            <Typography
              sx={{ fontSize: 14, whiteSpace: "pre-line", wordBreak: "break-word", mt: 0.5 }}
            >
              {log.note}
            </Typography>
          )}
        </Box>
      </Box>

      {/* deducts มีค่าเฉพาะ log ที่ action = REVIEW และ result = APPROVE */}
      {log.deducts?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <DeductTable deducts={log.deducts} approvedAmount={log.approved_amount} />
        </Box>
      )}

      {/*
        ใบเสร็จของผู้เช่า/แผงในคำร้องนี้ — เฉพาะรายการที่อนุมัติ ตามดีไซน์
        ผูกกับ receiptScope ที่ผู้เรียกส่งมา เพราะเส้นใบเสร็จเป็นของฝั่งเจ้าหน้าที่เท่านั้น
      */}
      {log.result === "APPROVE" && receiptScope && (
        <Box sx={{ mt: 2 }}>
          <ReceiptTable userId={receiptScope.userId} stallId={receiptScope.stallId} />
        </Box>
      )}

      {log.files?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 16 }}>ไฟล์แนบจากเจ้าหน้าที่</Typography>
          {log.files.map(renderFile)}
        </Box>
      )}
    </Box>
  );

  const renderBody = () => (
    <Box
      className="custom-scroll"
      sx={{ px: { xs: 2, md: 3 }, pb: 1, maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}
    >
      {subTitle && (
        <Typography sx={{ fontSize: 16, color: colors.textLightGray, textAlign: "center", mb: 2 }}>
          {subTitle}
        </Typography>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={32} sx={{ color: colors.themeMainColor }} />
        </Box>
      ) : logs.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
            borderRadius: "8px",
            border: `1px solid ${colors.lightGray}`,
            backgroundColor: colors.white,
          }}
        >
          <Typography sx={{ color: colors.textLightGray }}>ยังไม่มีการพิจารณาคำร้องนี้</Typography>
        </Box>
      ) : (
        // ตัด margin ล่างของการ์ดสุดท้ายออก ไม่ให้มีช่องว่างเกินก่อนขอบ modal
        <Box sx={{ "& > div:last-of-type": { mb: 0 } }}>{logs.map(renderLog)}</Box>
      )}
    </Box>
  );

  return (
    <ModalCustom
      // ต้องเป็น true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
      isShowProps
      // ปิด modal เองผ่าน onClose ไม่ให้ไป reset redux modal.isShow ของ modal อื่นในหน้าเดียวกัน
      autoResetModal={false}
      size="lg"
      closeButton
      title="ประวัติการพิจารณา"
      component={renderBody()}
      // ไม่ส่ง onSubmit/textBtnConfirm footer จึงไม่มีปุ่ม เหลือแค่ X มุมขวาบนตามดีไซน์
      onClose={onClose}
    />
  );
}
